import { Component, computed, EventEmitter, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { AbstractControl, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, debounceTime, map, startWith, Subscription } from 'rxjs';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';
import { extractXmlProps, upsertChildren } from '../../../../utils/xmlParser';
import { evaluateFormula, getFormulaRelevantIds } from '../../../../utils/formulaTransformers/parseFormulas';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';
import { NgStyle } from '@angular/common';

interface CalculatedValueItemProperties {
  text: string;
  calculatedValue: string;
  equationFormula: string;
  valueCaptionLeft: string;
  valueCaptionRight: string;
  captionWidth: string;
  style: string;
}

@Component({
  selector: 'lfe-calculated-value-item-form',
  imports: [FormsModule, ReactiveFormsModule, NgStyle],
  templateUrl: './calculated-value-item-form.component.html',
  standalone: true,
  styleUrl: './calculated-value-item-form.component.css'
})
export class CalculatedValueItemFormComponent
  extends iHasCaptionLeft(InteractiveItemComponent<CalculatedValueItemProperties>)
  implements OnInit, OnDestroy, IBaseItem<CalculatedValueItemProperties>, IHasCaptionLeft
{
  @Output() updateFirstColumnWidthRequired = new EventEmitter<{ id: string; width: number }>();

  formControl: FormControl<string | null> | undefined;
  private formulaSubscription?: Subscription;
  private valueChangeSubscription?: Subscription;

  properties: InteractiveItemProperties & CalculatedValueItemProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    calculatedValue: '',
    equationFormula: '',
    valueCaptionLeft: '',
    valueCaptionRight: '',
    captionWidth: '',
    style: 'Normal'
  };

  firstColumnWidth = computed(
    () =>
      this.sharedFirstColumnWidths()[this.firstSiblingItemId()] ||
      getOptimalFirstColumnWidth(this.properties.valueCaptionLeft || this.properties.text)
  );
  currentTextColor = signal('');

  formControlValue = signal('');
  resultTime = signal(new Date());

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `calculated_value_${this.currentId()}`);

  override ngOnInit(): void {
    super.ngOnInit();
    this.formControlId = this.properties.customId || this.properties.id;
    const initialValue = this.properties.calculatedValue;
    this.formControl = new FormControl(initialValue);

    this.form().addControl(this.formControlId, this.formControl);

    this.formControlValue.set(initialValue);

    this.valueChangeSubscription = this.formControl?.valueChanges.subscribe((value) => {
      this.formControlValue.set(value ?? '');
      this.resultTime.set(new Date());
    });

    this.setupFormulaCalculation();

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  getTextStyle(): { [key: string]: string } | null {
    if (!this.properties.valueCaptionLeft && !this.properties.text) {
      return null;
    }

    return {
      width: `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block'
    };
  }

  getTextTitle(): string | null {
    if (!this.properties.text) {
      return null;
    }
    if (this.firstColumnWidth() <= 0) {
      return null;
    }
    return this.properties.text;
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.formulaSubscription?.unsubscribe();
    this.valueChangeSubscription?.unsubscribe();
  }

  override extractLocalProperties(xml: Element): InteractiveItemProperties & CalculatedValueItemProperties {
    const properties = extractXmlProps(xml);

    const isGenericRepeatItem = properties['IsGenericRepeatItem'] === 'True';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id: properties['ID'],
      customId: properties['CustomID'],
      activationRule: properties['ActivationRule'],
      resultValue: properties['ResultValue'],
      resultUser: properties['ResultUser'],
      resultTime: properties['ResultTime'],
      text: properties['Text'],
      calculatedValue: properties['CalculatedValue'],
      equationFormula: properties['EquationFormula'],
      valueCaptionLeft: properties['ValueCaptionLeft'],
      valueCaptionRight: properties['ValueCaptionRight'],
      captionWidth: properties['CaptionWidth'],
      style: properties['Style'],
      isGenericRepeatItem
    };
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    const calculatedValue = this.formControlValue();
    this.properties.resultValue = this.formControlValue();
    this.properties.resultTime = this.resultTime().toISOString();

    upsertChildren(element, {
      CalculatedValue: calculatedValue || ''
    });

    return super.getXML(element);
  }

  private setupFormulaCalculation(): void {
    if (!this.properties.equationFormula) {
      return;
    }
    const ids = getFormulaRelevantIds(this.properties.equationFormula);

    const ctrls = ids.map((id) => this.form().get(id)).filter((c): c is AbstractControl => !!c);

    if (ctrls.length === 0) {
      return;
    }

    const streams = ctrls.map((c, index) =>
      c.valueChanges.pipe(
        startWith(c.value),
        map((value) => ({ id: ids[index], control: c, value }))
      )
    );

    this.formulaSubscription = combineLatest(streams)
      .pipe(debounceTime(300))
      .subscribe((controlData) => {
        const result = evaluateFormula(this.properties.equationFormula, controlData);
        this.onFormulaEvaluated(result);
      });
  }

  private onFormulaEvaluated(result: any): void {
    const stringResult = String(result ?? '');
    const currentValue = this.formControlValue();

    if (stringResult !== currentValue) {
      // Update the FormControl value and allow it to emit valueChanges
      // so other components (like ActivationRules) can react to the calculated value
      this.formControl?.setValue(stringResult);
      this.formControlValue.set(stringResult);
      this.resultTime.set(new Date());
    }
  }
}
